import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial, trackDemoRequest, trackPricingView } from '../utils/tracking';

/* ── Savings Calculator ─────────────────────────────────────── */
const tools = [
  { id: 'zoominfo',     name: 'ZoomInfo',                    icon: '🔵', baseCost: 14995, perUser: 1500,  note: 'Platform + per seat' },
  { id: 'apollo',       name: 'Apollo.io Pro',               icon: '🟠', baseCost: 0,     perUser: 1188,  note: 'Per seat / year' },
  { id: 'li_sn',        name: 'LinkedIn Sales Navigator',    icon: '🔷', baseCost: 0,     perUser: 1140,  note: 'Per seat / year' },
  { id: 'li_recruiter', name: 'LinkedIn Recruiter',          icon: '🔷', baseCost: 0,     perUser: 8999,  note: 'Per seat / year' },
  { id: 'seamless',     name: 'Seamless.ai Pro',             icon: '⚫', baseCost: 0,     perUser: 1428,  note: 'Per seat / year' },
  { id: 'contactout',   name: 'ContactOut',                  icon: '🟢', baseCost: 0,     perUser: 960,   note: 'Per seat / year' },
];

const kninePlans = [
  { maxUsers: 2,  name: 'Professional', cost: 6000  },
  { maxUsers: 10, name: 'Business',     cost: 10000 },
  { maxUsers: 999, name: 'Enterprise',  cost: null  },
];

const SavingsCalculator = () => {
  const [users, setUsers] = useState(3);
  const [selected, setSelected] = useState({});

  const toggle = (id) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const currentCost = tools.reduce((sum, t) => {
    if (!selected[t.id]) return sum;
    return sum + t.baseCost + t.perUser * users;
  }, 0);

  const kninePlan = kninePlans.find((p) => users <= p.maxUsers);
  const savings = kninePlan.cost ? currentCost - kninePlan.cost : null;
  const savingsPct = currentCost > 0 && savings > 0
    ? Math.round((savings / currentCost) * 100)
    : null;
  const anySelected = Object.values(selected).some(Boolean);

  return (
    <section className="bg-off-white">
      <div className="container">
        <div className="text-center" style={{ marginBottom: '48px' }}>
          <div className="section-badge blue">💰 Savings Calculator</div>
          <h2 className="section-title" style={{ fontSize: '2rem' }}>
            See How Much You Could <span className="text-gradient">Save</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Compare your current data &amp; sourcing spend against kNine. Select the tools your team uses today.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', maxWidth: '960px', margin: '0 auto' }}>

          {/* ── Left: inputs ── */}
          <div>
            {/* User count */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px', fontSize: '0.95rem' }}>
                How many users / seats?
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <input
                  type="range"
                  min="1" max="25" step="1"
                  value={users}
                  onChange={(e) => setUsers(Number(e.target.value))}
                  className="credit-slider-input"
                  style={{ margin: 0, flex: 1 }}
                />
                <span style={{ fontWeight: 800, fontSize: '1.75rem', color: 'var(--primary)', minWidth: '72px', textAlign: 'right' }}>
                  {users}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                <span>1</span><span>25</span>
              </div>
            </div>

            {/* Tool selector */}
            <label style={{ display: 'block', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px', fontSize: '0.95rem' }}>
              Which tools do you currently use?
            </label>
            {tools.map((tool) => {
              const toolCost = tool.baseCost + tool.perUser * users;
              const active = !!selected[tool.id];
              return (
                <div
                  key={tool.id}
                  onClick={() => toggle(tool.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '13px 16px',
                    marginBottom: '8px',
                    borderRadius: 'var(--radius)',
                    border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    background: active ? 'var(--primary-bg)' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    userSelect: 'none',
                  }}
                >
                  {/* checkbox */}
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                    border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    background: active ? 'var(--primary)' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.18s',
                  }}>
                    {active && <span style={{ color: 'white', fontSize: '11px', fontWeight: 800 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.9rem' }}>
                      {tool.icon} {tool.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      ~${toolCost.toLocaleString()}/yr for {users} {users === 1 ? 'seat' : 'seats'} &nbsp;·&nbsp; {tool.note}
                    </div>
                  </div>
                  {active && (
                    <div style={{ fontWeight: 700, color: '#ef4444', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      +${toolCost.toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Right: result card ── */}
          <div>
            <div className="card" style={{ padding: '36px', textAlign: 'center', position: 'sticky', top: '96px' }}>
              {!anySelected ? (
                <div style={{ padding: '40px 0' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👈</div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                    Select the tools your team currently uses to see your potential annual savings with kNine.
                  </p>
                </div>
              ) : (
                <>
                  {/* Current spend */}
                  <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                      Current Annual Spend
                    </div>
                    <div style={{ fontSize: '2.75rem', fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>
                      ${currentCost.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                      estimated per year · {users} {users === 1 ? 'seat' : 'seats'}
                    </div>
                  </div>

                  {/* kNine cost */}
                  <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                      kNine {kninePlan.name} Plan
                    </div>
                    <div style={{ fontSize: '2.75rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>
                      {kninePlan.cost ? `$${kninePlan.cost.toLocaleString()}` : 'Custom'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                      per year · up to {kninePlan.maxUsers < 999 ? kninePlan.maxUsers : '∞'} users
                    </div>
                  </div>

                  {/* Savings */}
                  {savings !== null && savings > 0 ? (
                    <div style={{ background: 'var(--success-bg)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
                        Your Annual Savings
                      </div>
                      <div style={{ fontSize: '2.75rem', fontWeight: 900, color: 'var(--success)', lineHeight: 1 }}>
                        ${savings.toLocaleString()}
                      </div>
                      {savingsPct && (
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--success)', marginTop: '6px' }}>
                          Save {savingsPct}% vs your current stack
                        </div>
                      )}
                    </div>
                  ) : kninePlan.cost && savings !== null ? (
                    <div style={{ background: 'var(--primary-bg)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '20px' }}>
                      <div style={{ color: 'var(--primary)', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.6 }}>
                        kNine delivers comparable — and often greater — capabilities at a competitive price, plus AI-native sourcing your current stack likely doesn't include.
                      </div>
                    </div>
                  ) : null}

                  <a
                    href="https://app.knine.ai/register"
                    className="btn btn-gradient btn-lg"
                    style={{ width: '100%', marginBottom: '10px' }}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackFreeTrial('savings_calculator')}
                  >
                    Start Saving — Free Trial
                  </a>
                  <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    * Estimates based on published list pricing. Actual costs vary by contract and negotiation.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Main Pricing page ──────────────────────────────────────── */
const Pricing = () => {
  const [annual, setAnnual] = useState(true);
  const [credits, setCredits] = useState(5000);

  useEffect(() => { trackPricingView(); }, []);

  const getRecommendation = (val) => {
    if (val <= 5000) return { plan: 'Professional', color: 'var(--primary)' };
    if (val <= 10000) return { plan: 'Business', color: 'var(--accent)' };
    return { plan: 'Enterprise', color: 'var(--text-dark)' };
  };

  const recommendation = getRecommendation(credits);

  const plans = [
    {
      name: 'Professional',
      desc: 'For individual recruiters and small teams getting started with AI sourcing.',
      priceAnnual: 6000,
      priceMonthly: 550,
      period: annual ? '/year' : '/mo',
      features: [
        'Up to 2 users',
        '5,000 credits per year',
        'kNine Verified™ email validation + phone type/carrier ID',
        'Chrome Extension',
        'AI-Powered Search',
        'Custom Integrations',
        'ATS/CRM Connection',
        'Analytics Dashboard',
        'Email & chat support',
      ],
      cta: 'Start Free Trial',
      ctaLink: 'https://app.knine.ai/register',
      ctaExternal: true,
      featured: false,
      trial: true,
    },
    {
      name: 'Business',
      desc: 'For growing teams that need more credits and deeper integrations.',
      priceAnnual: 10000,
      priceMonthly: 917,
      period: annual ? '/year' : '/mo',
      features: [
        'Up to 10 users',
        '10,000 credits per year',
        'Everything in Professional',
        'Priority Custom Integrations',
        'Advanced ATS/CRM Integration',
        'Team management tools',
        'Advanced analytics & reporting',
        'Priority support',
      ],
      cta: 'Get Started',
      ctaLink: '/contact',
      featured: true,
      trial: false,
    },
    {
      name: 'Enterprise',
      desc: 'For large organizations needing maximum capacity and custom features.',
      priceAnnual: null,
      priceMonthly: null,
      period: '',
      features: [
        'Unlimited users',
        'Up to 100,000 credits per year',
        'Everything in Business',
        'Tracker platform access',
        'Fee Recovery platform',
        'Auto enrichment & insights',
        'Custom integrations',
        'Dedicated account manager',
        'SLA & custom onboarding',
      ],
      cta: 'Contact Sales',
      ctaLink: '/contact',
      featured: false,
      trial: false,
    },
  ];

  return (
    <>
      {/* Header */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up">
          <div className="section-badge blue">💰 Pricing</div>
          <h1 className="hero-title" style={{ fontSize: '2.75rem' }}>
            Simple, Transparent <span className="text-gradient">Pricing</span>
          </h1>
          <p className="hero-subtitle">
            Choose the plan that fits your pack. All plans include our core AI sourcing platform.
            Start with a free trial — no credit card required.
          </p>
        </div>
      </section>

      {/* Credit Slider */}
      <section style={{ paddingTop: 0, paddingBottom: '24px' }}>
        <div className="container">
          <div className="credit-slider-section fade-up">
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)' }}>
              How many credits do you need?
            </h3>
            <div className="credit-slider-value">
              {credits >= 100000 ? '100,000+' : credits.toLocaleString()}
            </div>
            <div className="credit-slider-label">credits per year</div>
            <input
              type="range"
              min="1000"
              max="100000"
              step="1000"
              value={credits}
              onChange={(e) => setCredits(Number(e.target.value))}
              className="credit-slider-input"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: '500px', margin: '0 auto 24px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <span>1,000</span>
              <span>25,000</span>
              <span>50,000</span>
              <span>100,000</span>
            </div>
            <div className="credit-recommendation">
              🐕 We recommend: <strong>{recommendation.plan}</strong>
            </div>
          </div>
        </div>
      </section>

      {/* Billing Toggle */}
      <section style={{ paddingTop: 0, paddingBottom: '0' }}>
        <div className="container">
          <div className="pricing-toggle">
            <span
              className={`pricing-toggle-label ${!annual ? 'active' : ''}`}
              onClick={() => setAnnual(false)}
            >
              Monthly
            </span>
            <div
              className={`pricing-toggle-switch ${annual ? 'active' : ''}`}
              onClick={() => setAnnual(!annual)}
            />
            <span
              className={`pricing-toggle-label ${annual ? 'active' : ''}`}
              onClick={() => setAnnual(true)}
            >
              Annual
            </span>
            {annual && <span className="pricing-save-badge">Save 10% vs monthly</span>}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section style={{ paddingTop: '24px' }}>
        <div className="container">
          <div className="pricing-grid">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                style={
                  recommendation.plan === plan.name
                    ? { borderColor: 'var(--primary)', boxShadow: '0 4px 20px rgba(30,136,229,0.12)' }
                    : {}
                }
              >
                {recommendation.plan === plan.name && !plan.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '-14px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--success)',
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '4px 14px',
                    borderRadius: '100px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}>
                    Recommended For You
                  </div>
                )}
                <div className="pricing-card-name">{plan.name}</div>
                <div className="pricing-card-desc">{plan.desc}</div>

                {plan.priceAnnual !== null ? (
                  <>
                    <div className="pricing-card-price">
                      ${annual
                        ? plan.priceAnnual.toLocaleString()
                        : plan.priceMonthly.toLocaleString()
                      }
                      <span className="pricing-card-period">{plan.period}</span>
                    </div>
                    {annual && (
                      <div className="pricing-card-annual">
                        or ${plan.priceMonthly.toLocaleString()}/mo billed monthly
                      </div>
                    )}
                    {!annual && (
                      <div className="pricing-card-annual">
                        ${plan.priceAnnual.toLocaleString()}/year if billed annually
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="pricing-card-price" style={{ fontSize: '2rem' }}>
                      Custom
                    </div>
                    <div className="pricing-card-annual">
                      Tailored to your organization's needs
                    </div>
                  </>
                )}

                <div className="pricing-card-divider" />

                {plan.features.map((feature) => (
                  <div key={feature} className="pricing-feature">
                    <div className="pricing-feature-icon">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}

                <div style={{ marginTop: '24px' }}>
                  {plan.ctaExternal ? (
                    <a
                      href={plan.ctaLink}
                      className={`btn ${plan.featured ? 'btn-gradient' : 'btn-primary'} btn-lg`}
                      style={{ width: '100%' }}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackFreeTrial('pricing_card')}
                    >
                      {plan.cta}
                    </a>
                  ) : (
                    <Link
                      to={plan.ctaLink}
                      className={`btn ${plan.featured ? 'btn-gradient' : 'btn-primary'} btn-lg`}
                      style={{ width: '100%' }}
                      onClick={() => trackDemoRequest('pricing_card')}
                    >
                      {plan.cta}
                    </Link>
                  )}
                  {plan.trial && (
                    <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      14-day free trial included
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Feature Comparison */}
          <div style={{ marginTop: '80px' }}>
            <h3 className="section-title text-center" style={{ fontSize: '1.75rem', marginBottom: '32px' }}>
              Compare Plan Features
            </h3>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Feature</th>
                    <th>Professional</th>
                    <th className="highlight">Business</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Users',                    pro: 'Up to 2',     biz: 'Up to 10',  ent: 'Unlimited'    },
                    { feature: 'Annual Credits',           pro: '5,000',       biz: '10,000',    ent: 'Up to 100,000'},
                    { feature: 'Chrome Extension',         pro: '✓',           biz: '✓',         ent: '✓'            },
                    { feature: 'AI-Powered Search',        pro: '✓',           biz: '✓',         ent: '✓'            },
                    { feature: 'kNine Verified™ Email (Real-Time Deliverability Check)', pro: '✓', biz: '✓', ent: '✓'   },
                    { feature: 'Phone Type & Carrier ID (Twilio Lookup)',            pro: '✓', biz: '✓', ent: '✓'   },
                    { feature: 'Custom Integrations',       pro: '✓',           biz: 'Priority',  ent: 'Custom'       },
                    { feature: 'ATS/CRM Connection',       pro: 'Basic',       biz: 'Advanced',  ent: 'Custom'       },
                    { feature: 'Analytics',                pro: 'Standard',    biz: 'Advanced',  ent: 'Enterprise'   },
                    { feature: 'Tracker Platform',         pro: '—',           biz: '—',         ent: '✓'            },
                    { feature: 'Fee Recovery',             pro: '—',           biz: '—',         ent: '✓'            },
                    { feature: 'Auto Enrichment',          pro: '—',           biz: '—',         ent: '✓'            },
                    { feature: 'Custom Onboarding',        pro: '—',           biz: '—',         ent: '✓'            },
                    { feature: 'Dedicated Account Manager',pro: '—',           biz: '—',         ent: '✓'            },
                    { feature: 'Support',                  pro: 'Email & Chat',biz: 'Priority',  ent: '24/7 Dedicated'},
                  ].map((row) => (
                    <tr key={row.feature}>
                      <td className="feature-name">{row.feature}</td>
                      <td>{row.pro === '✓' ? <span className="check-icon">✓</span> : row.pro === '—' ? <span className="cross-icon">—</span> : row.pro}</td>
                      <td>{row.biz === '✓' ? <span className="check-icon">✓</span> : row.biz === '—' ? <span className="cross-icon">—</span> : row.biz}</td>
                      <td>{row.ent === '✓' ? <span className="check-icon">✓</span> : row.ent === '—' ? <span className="cross-icon">—</span> : row.ent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <SavingsCalculator />

      {/* FAQ */}
      <section>
        <div className="container-sm">
          <h3 className="section-title text-center" style={{ fontSize: '1.75rem', marginBottom: '36px' }}>
            Frequently Asked Questions
          </h3>
          {[
            {
              q: 'What are credits?',
              a: 'Credits are used each time you retrieve contact information for a prospect. One credit equals one contact lookup — including email, phone, and company data.'
            },
            {
              q: 'What does "kNine Verified" mean?',
              a: 'Every email kNine surfaces is checked in real time for deliverability, and every phone number is checked via Twilio Lookup to identify its type (mobile, landline, or VoIP) and carrier — across every plan. See how it works on our homepage.'
            },
            {
              q: 'Can I change plans later?',
              a: 'Absolutely. You can upgrade or downgrade your plan at any time. When upgrading, you\'ll be prorated for the remainder of your billing period.'
            },
            {
              q: 'Is there a free trial?',
              a: 'Yes! Our Professional plan includes a 14-day free trial. No credit card required to start — just sign up and begin sourcing.'
            },
            {
              q: 'What ATS/CRM systems do you integrate with?',
              a: 'We integrate with PCRecruiter, Crelate, Stardex, BigBiller/TE Recruit, Monday, HubSpot, RingCentral, Kruiterly, and more. Visit our Integrations page for the full list.'
            },
            {
              q: 'What is the Tracker platform?',
              a: 'Tracker is our fee recovery detection tool available on Enterprise plans. It automatically monitors and identifies potential fee recovery opportunities from your placed candidates.'
            },
          ].map((faq) => (
            <div key={faq.q} className="card-flat" style={{ marginBottom: '12px', padding: '24px 28px' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                {faq.q}
              </h4>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Not Sure Which Plan Is Right?</h2>
            <p>Talk to our team — we'll help you find the perfect fit for your sourcing needs.</p>
            <Link to="/contact" className="btn btn-white btn-lg" style={{ border: 'none' }}>
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
